import { clearPracticeSession } from '../practice/usePracticeRequest';
import { useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, changePassword, logout } from '../lib/rpc';
import { meQueryKey } from '../lib/queries';
import { useDesk } from '../desk/context';
import { AppWindow } from '../window/AppWindow';
import { PasswordField } from '../auth/PasswordField';
import { setReader, t } from '../messages';
import './profile.css';

/**
 * « Mon compte » : qui je suis, mes classes, changer mon mot de passe, me déconnecter.
 *
 * C'est une page **du cœur**, donc elle vit dans une fenêtre du cœur, comme une app — même barre
 * de titre, mêmes trois pastilles, même plein écran. Rien de spécial ne lui est accordé.
 *
 * Les deux formulaires appellent de vraies routes (`/api/auth/change-password`,
 * `/api/auth/logout`). Aucun bouton d'apparat.
 *
 * La mise en page : **une colonne centrée** (`.profile-column`), pas une colonne collée au bord
 * gauche d'une fenêtre de 1120px. C'était le défaut signalé — « tout est décalé à gauche, c'est
 * pas beau » — et il venait d'un `max-width` sans centrage. Les trois blocs ont donc la même
 * largeur, les libellés la même colonne, et chaque bloc finit par sa commande, alignée à droite.
 *
 * ## Ce que le parcours du 7 septembre 2026 a corrigé ici
 *
 * 1. **La règle du mot de passe est dite AVANT la saisie** (`profile.passwordRule`), pas découverte
 *    en échouant. Elle est appliquée côté serveur (`checkNewPassword`, `@ndrc/core-auth`) : cet
 *    écran l'annonce, il ne la juge pas — un écran qui la rejouerait finirait par diverger d'elle,
 *    et c'est le serveur qui a le dernier mot de toute façon.
 * 2. **Un clic répond toujours quelque chose.** L'état `feedback` porte un `seq` qui augmente à
 *    chaque tentative : recliquer avec les mêmes champs vides remonte un nœud neuf, donc le
 *    bandeau réapparaît et `role="alert"` le réannonce, au lieu d'un écran parfaitement immobile
 *    qui laisse croire que le bouton est mort.
 * 3. **Le bandeau de première connexion n'est plus rouge.** Changer un mot de passe provisoire est
 *    une étape normale, pas une panne — `.ndrc-notice` (bleu) et non `.ndrc-error`.
 */
const ROLE_LABEL = {
  student: 'profile.roleStudent',
  teacher: 'profile.roleTeacher',
  admin: 'profile.roleAdmin',
} as const;

/** Ce que l'écran répond au dernier clic. `seq` n'est jamais affiché : il sert de `key` au bandeau
 * pour que deux réponses identiques d'affilée restent deux réponses visibles. */
interface Feedback {
  kind: 'error' | 'ok';
  text: string;
  seq: number;
}

export function ProfilePage(): JSX.Element {
  const { me, className } = useDesk();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [busy, setBusy] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const attempts = useRef(0);
  const formRef = useRef<HTMLFormElement>(null);

  /** Répond au clic. `attempts` garantit un `seq` neuf à chaque appel : c'est ce qui rend une
   * réponse identique à la précédente visible quand même. */
  function answer(kind: Feedback['kind'], text: string): void {
    attempts.current += 1;
    setFeedback({ kind, text, seq: attempts.current });
  }

  /** Ramène le curseur sur le premier champ à remplir : la réponse la plus concrète possible à un
   * clic sur un formulaire incomplet, en plus de la phrase. */
  function focusFirstEmpty(): void {
    const fields = formRef.current?.querySelectorAll<HTMLInputElement>('input[type]');
    for (const field of fields ?? []) {
      if (field.value === '') {
        field.focus();
        return;
      }
    }
  }

  async function onChangePassword(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (current === '' || next === '' || confirm === '') {
      answer('error', t('profile.passwordEmpty'));
      focusFirstEmpty();
      return;
    }
    if (next !== confirm) {
      answer('error', t('profile.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      answer('ok', t('profile.passwordDone'));
      await queryClient.invalidateQueries({ queryKey: meQueryKey });
    } catch (caught) {
      // Le refus de la règle arrive du serveur avec sa propre phrase française, qui dit déjà
      // combien de caractères manquent et donne un exemple : on l'affiche telle quelle.
      answer('error', caught instanceof ApiError ? caught.message : t('desk.errorTitle'));
    } finally {
      setBusy(false);
    }
  }

  async function onLogout(): Promise<void> {
    setLeaving(true);
    try {
      await logout();
    } finally {
      if (me.role !== 'student') clearPracticeSession(me.id);
      queryClient.clear();
      // Le cache de session est vidé ; le lecteur l'est aussi. Sans cette ligne, l'écran de
      // connexion suivant — et le début de la session suivante, avant que `me` ne réponde —
      // hériterait du rôle de la personne qui vient de partir.
      setReader('student');
      await navigate('/login', { replace: true });
    }
  }

  return (
    <AppWindow
      title={t('profile.title')}
      {...(className === undefined ? {} : { subtitle: className })}
    >
      <div className="profile">
        <div className="profile-column">
          {me.mustChangePassword ? <p className="ndrc-notice">{t('profile.mustChange')}</p> : null}

          <section className="profile-block">
            <h2 className="profile-heading">{t('profile.identity')}</h2>
            <dl className="profile-list">
              <dt>{t('profile.name')}</dt>
              <dd>{me.displayName}</dd>
              <dt>{t('profile.role')}</dt>
              <dd>{t(ROLE_LABEL[me.role])}</dd>
              <dt>{t('profile.classes')}</dt>
              <dd>
                {me.classes.length === 0
                  ? t('profile.noClass')
                  : me.classes.map((k) => k.name).join(', ')}
              </dd>
            </dl>
          </section>

          <form
            className="profile-block"
            ref={formRef}
            onSubmit={(event) => {
              void onChangePassword(event);
            }}
          >
            <h2 className="profile-heading">{t('profile.passwordTitle')}</h2>
            <p className="profile-hint">{t('profile.passwordHint')}</p>
            {/* La règle, dite avant la saisie. Elle reprend mot pour mot le nombre exigé par
                `PASSWORD_MIN_LENGTH` (`packages/core-auth/src/password-rule.ts`) : si ce nombre
                change là-bas, cette phrase change ici. */}
            <p className="profile-rule">{t('profile.passwordRule')}</p>
            <PasswordField
              label={t('profile.passwordCurrent')}
              autoComplete="current-password"
              value={current}
              onChange={setCurrent}
            />
            <PasswordField
              label={t('profile.passwordNew')}
              autoComplete="new-password"
              value={next}
              onChange={setNext}
            />
            <PasswordField
              label={t('profile.passwordConfirm')}
              autoComplete="new-password"
              value={confirm}
              onChange={setConfirm}
            />
            {feedback === null ? null : (
              <p
                key={feedback.seq}
                role="alert"
                className={feedback.kind === 'ok' ? 'ndrc-ok' : 'ndrc-error'}
              >
                {feedback.text}
              </p>
            )}
            <div className="profile-actions">
              <button type="submit" className="ndrc-button ndrc-button--primary" disabled={busy}>
                {busy ? t('profile.passwordSubmitting') : t('profile.passwordSubmit')}
              </button>
            </div>
          </form>

          <section className="profile-block">
            <h2 className="profile-heading">{t('profile.session')}</h2>
            <div className="profile-actions profile-actions--plain">
              <button
                type="button"
                className="ndrc-button"
                disabled={leaving}
                onClick={() => {
                  void onLogout();
                }}
              >
                {leaving ? t('profile.logoutBusy') : t('profile.logout')}
              </button>
            </div>
          </section>
        </div>
      </div>
    </AppWindow>
  );
}
